import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Lock, Globe, Link as LinkIcon, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface ExamShareDialogProps {
  examId: string | null;
  examTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ExamShareDialog = ({
  examId,
  examTitle,
  open,
  onOpenChange,
}: ExamShareDialogProps) => {
  const [localVisibility, setLocalVisibility] = useState<"private" | "public">("public");
  const [copied, setCopied] = useState(false);

  const handleSelect = (visibility: "private" | "public") => {
    setLocalVisibility(visibility);
  };

  const handleCopy = () => {
    if (!examId) return;
    const url = `${window.location.origin}/room/exam/${examId}/summary`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!examId) return null;

  const isPublic = localVisibility === "public";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-xl p-4 pb-0">
        <DialogHeader>
          <DialogTitle className="text-base text-foreground/70">
            Share "{examTitle}"
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-4 pb-0">
          <div className="overflow-hidden rounded-2xl border border-border">
            {/* Private */}
            <button
              className={cn(
                "flex w-full items-center justify-between p-4 transition-colors hover:bg-muted/50",
                localVisibility === "private" && "bg-muted/40"
              )}
              onClick={() => handleSelect("private")}
            >
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-muted p-2">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="space-y-0.5 text-left">
                  <div className="text-sm font-medium text-foreground">Private</div>
                  <div className="text-xs text-muted-foreground">
                    Viewable by yourself only
                  </div>
                </div>
              </div>
              {localVisibility === "private" && (
                <Check className="h-5 w-5 text-primary" />
              )}
            </button>

            {/* Public */}
            <button
              className={cn(
                "flex w-full items-center justify-between border-t border-border p-4 transition-colors hover:bg-muted/50",
                localVisibility === "public" && "bg-muted/40"
              )}
              onClick={() => handleSelect("public")}
            >
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-muted p-2">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="space-y-0.5 text-left">
                  <div className="text-sm font-medium text-foreground">Public</div>
                  <div className="text-xs text-muted-foreground">
                    Anyone with link can view
                  </div>
                </div>
              </div>
              {localVisibility === "public" && (
                <Check className="h-5 w-5 text-primary" />
              )}
            </button>
          </div>
        </div>

        <div className="px-1 py-4">
          <button
            onClick={handleCopy}
            disabled={!isPublic}
            className={cn(
              "flex h-10 w-full items-center justify-center rounded-lg text-sm font-medium transition-colors",
              isPublic
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "cursor-not-allowed bg-muted text-muted-foreground"
            )}
          >
            <LinkIcon className="mr-1.5 h-4 w-4" />
            {copied ? "Copied!" : "Copy Public Link"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
